/**
 * ContactSlotLink — AD-040 BATCH-1 (2026-06-25) per-row contact-spine control,
 * mirroring PropertySlotLink (AD-036) for the Design Programme's Coordination
 * (project team) section. UI contract: `AVBP_DESIGN_PROGRAMME_INTAKE_MOCKUP_2026-06-24.html` §H.
 *
 * Each coordination row is in exactly one of three states:
 *   1. LINKED  — `member.contactId` set → a compact `LINKED · C##### · name ·
 *                email · WhatsApp` row with Change / Unlink.
 *   2. CHOOSE  — no contact, no captured details → a single search input
 *                ("Link an existing contact — name / phone / email…") + a
 *                dropdown of matches; "— or — ＋ Add as a new contact" reveals
 *                the capture fields.
 *   3. NEW     — staff chose "add new" → Name / Email / WhatsApp fields with a
 *                LIVE duplicate-detect callout (fires off name/phone/email): the
 *                best spine match shows "Use this contact" vs "No — add new"
 *                (same UX as the property 25 m proximity "Use this").
 *
 * Persistence is best-effort, staff-confirmed, dedup-aware: linking an existing
 * contact role-tags it (`addContactRoleTag`); "add new" creates a role-tagged
 * contact (`createCoordinationContact`). The role-tag derives from the row's role.
 *
 * Pure dedup logic reuses `contactMatch` (matchContacts/bestAutoLink) — the same
 * AD-039 spine matcher the leads use. No duplication.
 */
import { useMemo, useState } from 'react';
import { Btn3, Tag } from './ui';
import {
  matchContacts,
  bestAutoLink,
  type ContactLite,
} from '../lib/contactMatch';
import {
  createCoordinationContact,
  addContactRoleTag,
} from '../lib/projectStore';
import { roleToTag, type CoordinationMember } from '../lib/designProgramme';

export interface ContactSlotLinkProps {
  member: CoordinationMember;
  /** All contacts (one shared listener in the parent). For search + dup-detect. */
  allContacts: ContactLite[];
  actorName: string;
  /** Patch THIS member (contactId / name / email / whatsapp). */
  onPatch: (patch: Partial<CoordinationMember>) => void;
  onMsg: (m: string, ok?: boolean) => void;
}

const fieldStyle: React.CSSProperties = {
  width: '100%', border: '1px solid var(--rule-strong, #cdd4da)', borderRadius: 'var(--r-sm)',
  padding: '8px 10px', fontSize: 13, background: '#fff',
};

export default function ContactSlotLink({
  member,
  allContacts,
  actorName,
  onPatch,
  onMsg,
}: ContactSlotLinkProps) {
  const linkedId = member.contactId?.trim() || '';
  const [search, setSearch] = useState('');
  const [enteringNew, setEnteringNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const linked = useMemo(
    () => allContacts.find((c) => c.cid === linkedId) ?? null,
    [allContacts, linkedId],
  );

  // CHOOSE-state search list (by name / phone / email).
  const searchResults = useMemo(() => {
    const q = search.trim();
    if (!q) return [];
    return matchContacts({ name: q, phone: q, email: q }, allContacts).slice(0, 20);
  }, [search, allContacts]);

  // NEW-state live duplicate-detect off the captured Name/Email/WhatsApp.
  const dupMatches = useMemo(() => {
    if (!enteringNew) return [];
    const input = {
      ...(member.name ? { name: member.name } : {}),
      ...(member.whatsapp ? { phone: member.whatsapp } : {}),
      ...(member.email ? { email: member.email } : {}),
    };
    if (!input.name && !input.phone && !input.email) return [];
    return matchContacts(input, allContacts).slice(0, 3);
  }, [enteringNew, member.name, member.email, member.whatsapp, allContacts]);
  const dupAuto = useMemo(() => bestAutoLink(dupMatches), [dupMatches]);

  async function doLink(c: ContactLite) {
    setBusy(true);
    try {
      await addContactRoleTag(c.cid, roleToTag(member.role), actorName);
      onPatch({
        contactId: c.cid,
        name: c.name,
        email: c.email || (c.emails?.[0]?.address ?? ''),
        whatsapp: c.phone || (c.phones?.[0]?.number ?? ''),
      });
      setEnteringNew(false);
      setSearch('');
      onMsg(`Linked ${c.clientNo} as ${member.role}.`, true);
    } catch {
      onMsg('Could not link the contact — check your connection.');
    } finally {
      setBusy(false);
    }
  }

  async function doCreate() {
    setBusy(true);
    try {
      const phones = member.whatsapp ? [{ number: member.whatsapp, label: 'whatsapp', whatsApp: true }] : [];
      const emails = member.email ? [{ address: member.email, label: 'primary' }] : [];
      const clientNo = await createCoordinationContact({
        name: member.name,
        phones,
        emails,
        roleTag: roleToTag(member.role),
        actorName,
      });
      onPatch({ contactId: clientNo });
      setEnteringNew(false);
      onMsg(`Created & linked ${clientNo} as ${member.role}.`, true);
    } catch {
      onMsg('Could not create the contact — check your connection.');
    } finally {
      setBusy(false);
    }
  }

  function doUnlink() {
    onPatch({ contactId: undefined });
    setEnteringNew(false);
    onMsg('Unlinked. Search to link another, or add a new contact.');
  }

  // ── LINKED ────────────────────────────────────────────────────────────────
  if (linkedId) {
    const label = [linked?.name || member.name, member.email, member.whatsapp].filter(Boolean).join(' · ') || linkedId;
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: 'var(--ok-wash, #eaf5ef)', border: '1px solid var(--ok-line, #bfe0cf)', borderRadius: 'var(--r-sm)', padding: '9px 11px' }}>
          <Tag kind="val">LINKED</Tag>
          <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{linked?.clientNo || linkedId}</span>
          <span style={{ fontSize: 12.5, flex: 1, minWidth: 120 }}>{label}</span>
          <Btn3 intent="ghost" size="sm" onClick={doUnlink} disabled={busy}>Change / Unlink</Btn3>
        </div>
        <div style={{ fontSize: 11, color: 'var(--ok, #1f7a5a)', marginTop: 6 }}>↳ existing contact reused — not duplicated.</div>
      </div>
    );
  }

  // ── NEW (capture + live dup-detect) ─────────────────────────────────────────
  if (enteringNew) {
    return (
      <div>
        <div style={{ fontSize: 11.5, fontWeight: 700, marginBottom: 7, color: 'var(--accent-deep, #274b63)' }}>＋ Add as a new contact</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, marginBottom: 4 }}>Name</label>
            <input style={fieldStyle} value={member.name} onChange={(e) => onPatch({ name: e.target.value })} placeholder="Full name" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, marginBottom: 4 }}>Email ID</label>
            <input style={fieldStyle} type="email" value={member.email || ''} onChange={(e) => onPatch({ email: e.target.value })} placeholder="name@studio.com" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, marginBottom: 4 }}>WhatsApp</label>
            <input style={fieldStyle} value={member.whatsapp || ''} onChange={(e) => onPatch({ whatsapp: e.target.value })} placeholder="+91 …" />
          </div>
        </div>

        {dupMatches.length > 0 && (
          <div style={{ border: '1px solid var(--warn-line, #eccf93)', background: 'var(--warn-wash, #fbf3e1)', borderRadius: 'var(--r-sm)', padding: '10px 12px', marginTop: 10 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--warn, #9a6a12)' }}>⚠ This might already be a contact</div>
            {dupMatches.map((m) => {
              const c = allContacts.find((x) => x.cid === m.contactId);
              if (!c) return null;
              return (
                <div key={m.contactId} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--accent)' }}>{m.clientNo}</span>
                  <span style={{ fontSize: 12.5, flex: 1 }}>{m.name} · {m.reasons.join(', ')}</span>
                  <Btn3 intent="grn" size="sm" onClick={() => doLink(c)} disabled={busy}>Use this contact</Btn3>
                </div>
              );
            })}
            <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 6 }}>
              {dupAuto ? 'Strong match on phone/email.' : 'Possible match.'} You decide — link the existing one, or confirm it's a new person below.
            </div>
          </div>
        )}

        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <Btn3 intent="grn" size="sm" onClick={doCreate} disabled={busy || !(member.name || member.email || member.whatsapp)}>
            {busy ? 'Saving…' : 'No — add as new contact'}
          </Btn3>
          <Btn3 intent="ghost" size="sm" onClick={() => { setEnteringNew(false); onPatch({ name: '', email: undefined, whatsapp: undefined }); }} disabled={busy}>Cancel</Btn3>
        </div>
      </div>
    );
  }

  // ── CHOOSE (search to link, or add new) ─────────────────────────────────────
  return (
    <div>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Link an existing contact — name / phone / email…"
          autoComplete="off"
          aria-label="Link an existing contact"
          style={{ width: '100%', padding: '10px 12px', fontSize: 13, border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)' }}
        />
        {search.trim() && (
          <div style={{ border: '1px solid var(--rule)', borderTop: 'none', borderRadius: '0 0 var(--r-sm) var(--r-sm)', background: 'var(--surface, #fff)', maxHeight: 220, overflowY: 'auto' }}>
            {searchResults.length > 0 ? (
              searchResults.map((m) => {
                const c = allContacts.find((x) => x.cid === m.contactId);
                if (!c) return null;
                return (
                  <button key={m.contactId} type="button" onClick={() => doLink(c)} disabled={busy}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '9px 12px', border: 'none', borderTop: '1px solid var(--rule)', background: 'transparent', cursor: 'pointer' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>{m.clientNo}</span>
                    <span style={{ fontSize: 13, flex: 1 }}>{m.name}<span style={{ color: 'var(--ink-mute)' }}> · {m.reasons.join(', ')}</span></span>
                  </button>
                );
              })
            ) : (
              <div style={{ padding: '9px 12px', fontSize: 12, color: 'var(--ink-mute)' }}>
                No contact matches "{search.trim()}". Use "Add a new contact".
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--ink-mute)', fontSize: 12, margin: '11px 0' }}>
        <span style={{ flex: 1, height: 1, background: 'var(--rule)' }} /> or <span style={{ flex: 1, height: 1, background: 'var(--rule)' }} />
      </div>
      <Btn3 intent="ghost" size="sm" onClick={() => setEnteringNew(true)} disabled={busy}>＋ Add as a new contact</Btn3>
    </div>
  );
}
